"use client";

import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

/**
 * Example prompts shown as an animated "type-on / backspace / retype" hint in
 * the home + pricing prompt boxes. Kept in one place so every box cycles the
 * same list. They all start with "Build a…" so the animation only rewinds to
 * the shared stem between phrases (see useTypewriterPlaceholder).
 */
export const PROMPT_HINT_PHRASES = [
  "Build a site for my marketing agency…",
  "Build a booking app for my salon…",
  "Build a CRM for my sales team…",
  "Build an online store for my brand…",
  "Build a membership site for my community…",
];

/**
 * The same examples with the leading verb dropped ("a site for my marketing
 * agency…"), for boxes that already lead with "Ask Panda to build:". Composing
 * that lead with the full phrases above reads "Ask Panda to build: Build a
 * site…" — the verb twice.
 *
 * Derived from the one list rather than hand-written as a second one, so a new
 * example can't be added to only half the boxes. Module scope keeps the
 * reference STABLE, which useTypewriterPlaceholder requires (a fresh array each
 * render would restart the animation on every keystroke).
 *
 * They still share the "a" stem, so the backspace-to-stem animation is
 * unaffected.
 */
export const PROMPT_HINT_TAILS = PROMPT_HINT_PHRASES.map((p) =>
  p.replace(/^Build /, ""),
);

const TYPE_MS = 45; // per-char delay while typing on
const DELETE_MS = 26; // per-char delay while backspacing
const HOLD_FULL_MS = 1900; // pause once a phrase is fully typed
const HOLD_STEM_MS = 320; // pause at the shared stem before the next phrase

/**
 * Number of leading characters two strings share — e.g. "Build a booking…" vs
 * "Build an online…" → "Build a" (7). The animation backspaces only down to
 * this shared stem instead of clearing the whole line, so the hint reads like
 * someone editing the tail of the sentence (Lovable-style).
 */
function sharedPrefixLength(a: string, b: string): number {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i += 1;
  return i;
}

/**
 * Lovable-style animated placeholder: types a phrase on, holds, backspaces to
 * the stem it shares with the next phrase, then types the next one — cycling
 * forever. Returns the current string to drop straight into a native
 * `placeholder` attribute.
 *
 * Returns the first phrase (static, no timers) when the user prefers reduced
 * motion, when fewer than two phrases are supplied, or when `enabled` is
 * false — pass `enabled: false` while the box already has text, since the
 * native placeholder isn't visible then anyway.
 *
 * @param phrases the lines to cycle (must be a STABLE reference — pass a
 *                module-level const, not an inline array literal)
 * @param enabled animate while true; hold the first phrase while false
 */
export function useTypewriterPlaceholder(
  phrases: string[],
  enabled: boolean = true,
): string {
  const reduceMotion = usePrefersReducedMotion();
  const [text, setText] = useState(phrases[0] ?? "");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only animate when there are ≥2 phrases to move between, motion is allowed,
  // and the box is empty. Otherwise the hook returns the first phrase static.
  const animate = enabled && !reduceMotion && phrases.length >= 2;

  // `phrases` must be a stable reference (see doc above) — the callers pass a
  // module-level const, so this effect runs on mount / when `animate` flips,
  // not per keystroke.
  useEffect(() => {
    if (!animate) return;

    let cancelled = false;
    let idx = 0;
    let charIdx = phrases[0].length;
    let mode: "type" | "delete" = "delete";
    let stem = sharedPrefixLength(phrases[0], phrases[1]);

    const schedule = (fn: () => void, ms: number) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (!cancelled) fn();
      }, ms);
    };

    const step = () => {
      if (cancelled) return;
      const current = phrases[idx % phrases.length];

      if (mode === "type") {
        if (charIdx < current.length) {
          charIdx += 1;
          setText(current.slice(0, charIdx));
          schedule(step, TYPE_MS);
        } else {
          // Fully typed → hold, then rewind toward the next phrase's stem.
          const next = phrases[(idx + 1) % phrases.length];
          stem = sharedPrefixLength(current, next);
          mode = "delete";
          schedule(step, HOLD_FULL_MS);
        }
        return;
      }

      // delete
      if (charIdx > stem) {
        charIdx -= 1;
        setText(current.slice(0, charIdx));
        schedule(step, DELETE_MS);
      } else {
        // Reached the shared stem → advance and type the next phrase's tail.
        idx += 1;
        mode = "type";
        schedule(step, HOLD_STEM_MS);
      }
    };

    // Kick off from inside the scheduled callback (never synchronously in the
    // effect body): show phrase 0 in full, hold, then begin the first rewind.
    // On a fresh mount `text` already equals phrase 0, so this just anchors the
    // starting frame when the animation (re)starts after being paused.
    schedule(() => {
      setText(phrases[0]);
      schedule(step, HOLD_FULL_MS);
    }, 0);

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [phrases, animate]);

  return animate ? text : (phrases[0] ?? "");
}
